import { getAccessToken } from './authStore';
import { safeGetSession } from './supabase';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL as string | undefined;

if (!apiBaseUrl) {
  throw new Error(
    'Missing REACT_APP_API_BASE_URL. Set it to your API Gateway base URL, e.g. https://.../dev/api'
  );
}

/** Lead info sent to the API for context. */
export interface RewriteLeadInfo {
  name?: string;
  company?: string;
  position?: string;
  contactValue?: string;
  sector?: string;
  notes?: string;
}

export interface RewritePayload {
  /** Source message text (may include variable tokens like {lead_name}). */
  content: string;
  /** Optional user instructions for the rewrite. */
  instruction?: string;
  /** Optional lead context (name, company, position, etc.) for personalization. */
  lead?: RewriteLeadInfo;
  /** Optional email subject (returned as-is if not rewritten). */
  subject?: string;
  /** When true, AI must keep all variable tokens/placeholders unchanged. */
  preserveVariableTokens?: boolean;
}

export interface RewriteResponse {
  subject: string;
  content: string;
}

async function jsonFetch<T>(input: RequestInfo, init?: RequestInit): Promise<T> {
  const token =
    getAccessToken() ||
    (await safeGetSession(4000)).data.session?.access_token ||
    undefined;
  if (!token) {
    throw new Error('Votre session a expiré. Veuillez vous reconnecter et réessayer.');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init?.headers as Record<string, string> || {}),
  };
  headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(input, { ...init, headers });

  if (!res.ok) {
    let message = `Request failed with status ${res.status}`;
    let code: string | undefined;
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
      if (body?.code) code = body.code;
    } catch {
      // ignore non-JSON bodies (API Gateway timeouts often return plain text / HTML)
    }
    // Lambda/API Gateway timeout or upstream gateway failure → clear UX code
    if (!code && (res.status === 502 || res.status === 503 || res.status === 504)) {
      code = 'TIMEOUT';
    }
    const err = new Error(message) as Error & { code?: string; status?: number };
    err.code = code;
    err.status = res.status;
    throw err;
  }

  return res.json() as Promise<T>;
}

export const aiRewriteService = {
  async rewrite(payload: RewritePayload): Promise<RewriteResponse> {
    return jsonFetch<RewriteResponse>(`${apiBaseUrl}/ai/rewrite`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};
